import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Upload, Loader, Video, XCircle } from "lucide-react";
import { auth, storage, db } from "../firebase";
import { useNavigate } from "react-router-dom";
// import { toast } from "react-hot-toast";
import { toast } from "react-toastify";
import { getDownloadURL, listAll, ref, uploadBytes } from "firebase/storage";
import { collection, query, orderBy, getDocs, doc, getDoc } from "firebase/firestore";

function VideoGenerator() {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [selectedDefaultPrompt, setSelectedDefaultPrompt] = useState("");
  const [engine, setEngine] = useState("vidu");
  const [processing, setProcessing] = useState(false);
  
  // Default prompts for StyleUAI 1.0 (vidu)
  const defaultPrompts = [
    "A person walking gracefully in a beautiful garden with flowers blooming",
    "A model showcasing elegant fashion in a modern city setting",
    "A person dancing with smooth, fluid movements in a studio"
  ];
  const [generationProgress, setGenerationProgress] = useState(0);
  const progressTimer = useRef(null);
  const [videoURL, setVideoURL] = useState("");
  const [userId, setUserId] = useState(null);
  const [assets, setAssets] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videoPage, setVideoPage] = useState(1);
  const videosPerPage = 6;

  const paginatedVideos = videos.slice(
    (videoPage - 1) * videosPerPage,
    videoPage * videosPerPage
  );

  
  const navigate = useNavigate();
  
  const [currentPage, setCurrentPage] = useState(1);
  const assetsPerPage = 8;
  const paginatedAssets = assets.slice(
    (currentPage - 1) * assetsPerPage,
    currentPage * assetsPerPage
  );
  
  
  const handlePrevVideoPage = () => {
    if (videoPage > 1) setVideoPage(videoPage - 1);
  };
  
  const handleNextVideoPage = () => {
    if (videoPage < totalVideoPages) setVideoPage(videoPage + 1);
  };
  
 const startProgress = () => {
  setGenerationProgress(0);
  progressTimer.current = setInterval(() => {
    setGenerationProgress((prev) => {
      const next = prev + 0.5 // smooth increase
      return next >= 99 ? 99 : next;
    });
  }, 1000);
};

  const totalPages = Math.max(1, Math.ceil(assets.length / assetsPerPage));
  const totalVideoPages = Math.max(1, Math.ceil(videos.length / videosPerPage));
  
  const stopProgress = (final = 100) => {
    if (progressTimer.current) {
      clearInterval(progressTimer.current);
      progressTimer.current = null;
    }
    setGenerationProgress(final);
    setTimeout(() => setGenerationProgress(0), 1000);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
        fetchUserAssets(user.uid);
        fetchGeneratedVideos(user.uid);
      } else {
        toast.error("Please log in to use this feature.");
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, []);

  

  const fetchUserAssets = async (uid) => {
    try {
      const outputRef = ref(storage, `assets/${uid}/outputs`);
      const res = await listAll(outputRef);
      const urls = await Promise.all(res.items.map((item) => getDownloadURL(item)));
      setAssets(urls.reverse());
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  };

const fetchGeneratedVideos = async (uid) => {
  if (!uid) return;

  try {
    setVideosLoading(true);

    // 🔹 Step 1: Try to load from Firestore first
    const videosRef = collection(db, "users", uid, "videos");
    let querySnapshot;

    try {
      const q = query(videosRef, orderBy("created_at", "desc"));
      querySnapshot = await getDocs(q);
    } catch (orderByError) {
      console.warn("OrderBy failed, falling back:", orderByError);
      querySnapshot = await getDocs(videosRef);
    }

    let videoData = [];

    if (!querySnapshot.empty) {
      // 🔹 Step 2: Build from Firestore docs
      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();

        let videoUrl = null;
        try {
          if (data.storage_path) {
            const storageRef = ref(storage, data.storage_path);
            videoUrl = await getDownloadURL(storageRef); // ✅ Always fresh
          }
        } catch (urlErr) {
          console.error("Failed to fetch video URL for:", data, urlErr);
        }

        videoData.push({
          id: docSnapshot.id,
          video_url: videoUrl,
          prompt: data.prompt || "",
          engine: data.engine || "",
          created_at: data.created_at || null,
          filename: data.filename || "",
          storage_path: data.storage_path || null,
        });
      }

      // 🔹 Step 3: Sort by created_at (newest first)
      videoData.sort((a, b) => {
        const timeA = a.created_at?.toDate
          ? a.created_at.toDate().getTime()
          : new Date(a.created_at).getTime();
        const timeB = b.created_at?.toDate
          ? b.created_at.toDate().getTime()
          : new Date(b.created_at).getTime();
        return timeB - timeA;
      });
    } else {
      // 🔹 Step 4: Fallback to listAll if Firestore empty
      console.log("No Firestore docs, falling back to Storage listAll...");
      const folderRef = ref(storage, `assets/${uid}/videos`);
      const result = await listAll(folderRef);

      videoData = await Promise.all(
        result.items.map(async (item) => ({
          id: item.fullPath,
          video_url: await getDownloadURL(item),
          filename: item.name,
          created_at: null,
          prompt: "",
          engine: "",
          storage_path: item.fullPath,
        }))
      );

      // Sort by filename (timestamp embedded)
      videoData.sort((a, b) => b.filename.localeCompare(a.filename));
    }

    // 🔹 Step 5: Update state
    setVideos(videoData.map((v) => v.video_url));
    setVideoPage(1);
    if (videoData.length) setVideoURL(videoData[0].video_url);

  } catch (err) {
    console.error("❌ Error fetching videos:", err);
  } finally {
    setVideosLoading(false);
  }
};
  // Replace pollUntilReady
const pollUntilReady = async (taskId) => {
  const maxRetries = 36;
  const interval = 5000;

  for (let i = 0; i < maxRetries; i++) {
    try {
      const res = await fetch(
        `https://video-generator.styleuai.com/api/video-status?task_id=${taskId}&user_id=${userId}&prompt=${encodeURIComponent(prompt)}`
      );
      // console.log(`⏳ Polling attempt ${i + 1} for task ${taskId}`);
      // const res = await fetch(
      //   `https://video-generator.styleuai.com/api/video-status?task_id=${taskId}&user_id=${userId}&prompt=${encodeURIComponent(prompt)}`
      // );
      const data = await res.json();

      // console.log("📡 Polling response:", data);

      if (data.status === "success") {
        await fetchGeneratedVideos(userId);
        stopProgress(100);
        toast.success("Video generated!");
        return;
      } else if (data.status === "failed") {
        stopProgress(0);
        toast.error("Video generation failed.");
        return;
      }
    } catch (err) {
      // console.warn("Polling error:", err);
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  stopProgress(0);
  toast.error("Video generation timed out.");
};


  const handleAssetSelect = async (imageUrl) => {
    setImage(imageUrl);
    setPreview(imageUrl);
    toast.success("Asset selected successfully!");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    } else {
      toast.error("Image must be under 10MB");
    }
  };

  async function uploadToFirebase(uid, file) {
    const path = `assets/${uid}/uploads/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  }

// Updated handleGenerate
async function handleGenerate() {
  if (!image) return toast.error("Please upload or select an image");

  try {
    // ✅ Check user credits before generating
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      toast.error("User not found.");
      return;
    }

    const currentCredits = userSnap.data().credits || 0;
    
    // Determine required credits based on engine
    const requiredCredits = engine === "vidu" ? 25 : 35; // veo3/seedance = 35, vidu = 25

    if (currentCredits < requiredCredits) {
      toast.error("Insufficient credits. Buy more.");
      return;
    }

    setProcessing(true);
    startProgress();

    let imageUrl;
    if (image instanceof File) {
      // Upload file to Firebase to get a public URL
      imageUrl = await uploadToFirebase(userId, image);
    } else {
      imageUrl = image;
    }

    // Use selected default prompt or custom prompt
    const finalPrompt = selectedDefaultPrompt || prompt;

    const res = await fetch("https://video-generator.styleuai.com/api/img2video", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        image: imageUrl,
        prompt: finalPrompt,
        user_id: userId,
        engine, // should be "vidu", "veo3", or "seedance"
      }),
    });

    const data = await res.json();
    console.log("📦 Response from /img2video:", data);

    if (!res.ok) {
      throw new Error(data.error || "Video generation failed");
    }

    // Common polling handler for async engines
    const handlePolling = async (taskId, selectedEngine) => {
      if (!taskId) throw new Error(`No task ID returned for ${selectedEngine} engine.`);

      let status = "processing";
      while (status === "processing") {
        await new Promise((resolve) => setTimeout(resolve, 8000)); // poll every 8s
        const statusRes = await fetch(
          `https://video-generator.styleuai.com/api/video-status?task_id=${taskId}&user_id=${userId}&prompt=${encodeURIComponent(finalPrompt)}&engine=${selectedEngine}`
        );
        const statusData = await statusRes.json();
        status = statusData.status;
        console.log(`📊 ${selectedEngine} status:`, statusData);

        if (status === "success") {
          stopProgress(100);
          toast.success("Video generated!");
          await fetchGeneratedVideos(userId);
          break;
        } else if (status === "failed") {
          throw new Error(`${selectedEngine} video generation failed`);
        }
      }
    };

    // Engine-specific handling
    if (engine === "vidu") {
      await handlePolling(data.task_id, "vidu");
    } 
    else if (engine === "veo3") {
      await handlePolling(data.task_id, "veo3");
    } 
    else if (engine === "seedance") {
      await handlePolling(data.task_id, "seedance");
    }

  } catch (err) {
    console.error("❌ Generation error:", err);
    stopProgress(0);
    toast.error(err.message || "Something went wrong!");
  } finally {
    setProcessing(false);
  }
}



  return (
    <div className="flex h-screen">
       <div className="hidden md:block">
    <Sidebar />
  </div>
      <div className="flex flex-col flex-1 bg-white">
    {/* Header always visible at top */}
  <div className="absolute top-2 md:top-4 right-2 md:right-4">
    <Header />
  </div>

    {/* Sidebar for mobile (only shown in small screens) */}
    <div className="block md:hidden">
      <Sidebar />
    </div>
    <div className="content-container">
        <div className="p-4 sm:p-6 flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-1/2">
            <h1 className="text-2xl font-bold mb-4 text-gray-800">AI Video Generator</h1>
            <p className="text-sm text-gray-600 mb-4">Upload or select an image and describe the scene to generate a video.</p>

            <div className="bg-white border rounded-xl p-6 shadow-sm">
              <div className="relative h-[260px] w-full border-2 border-dashed rounded-lg bg-[#F5F2FF] flex items-center justify-center">
                {preview ? (
                  <div className="relative w-full h-full">
                    <img src={preview} alt="Preview" className="w-full h-full object-contain p-2" />
                    <button
                      onClick={() => {
                        setPreview(null);
                        setImage(null);
                        document.getElementById("videoUpload").value = null;
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <label htmlFor="videoUpload" className="cursor-pointer text-center">
                    <Upload className="w-10 h-10 text-[#3913C5] mx-auto" />
                    <p className="text-gray-600 mt-2">Click to upload an image</p>
                    <p className="text-gray-400 text-sm">Max 10MB (JPG, PNG)</p>
                  </label>
                )}
                <input
                  type="file"
                  id="videoUpload"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {/* Prompt Selection for StyleUAI 1.0 */}
              {engine === "vidu" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Choose a prompt</label>
                  
                  {/* Quick Prompt Buttons */}
                  <div className="grid grid-cols-1 gap-2 mb-3">
                    {defaultPrompts.map((defaultPrompt, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setSelectedDefaultPrompt(defaultPrompt);
                          setPrompt(""); // Clear custom input when selecting default
                        }}
                        className={`p-3 text-left text-sm rounded-lg border transition-all ${
                          selectedDefaultPrompt === defaultPrompt
                            ? "border-[#3913C5] bg-[#F7F5FF] text-[#3913C5]"
                            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {defaultPrompt}
                      </button>
                    ))}
                  </div>

                  {/* Custom Prompt Input */}
                  <div className="relative">
                    <input
                      type="text"
                      value={prompt}
                      onChange={(e) => {
                        setPrompt(e.target.value);
                        if (e.target.value) {
                          setSelectedDefaultPrompt(""); // Clear default selection when typing custom
                        }
                      }}
                      placeholder="Or type your own custom prompt..."
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3913C5]"
                    />
                  </div>
                </div>
              )}

              {/* Custom Prompt Input for StyleUAI 2.0 */}
              {engine === "veo3" && (
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Optional: Describe the scene"
                  className="mt-4 p-3 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3913C5]"
                />
              )}

              {/* Engine Selector */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Choose Model</label>
                <select
                  value={engine}
                  onChange={(e) => {
                    setEngine(e.target.value);
                    // Clear prompt selections when switching engines
                    setSelectedDefaultPrompt("");
                    setPrompt("");
                  }}
                  className="p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-[#3913C5]"
                >
                  <option value="vidu">StyleUAI 1.0</option>
                  {/* <option value="seedance">StyleUAI 2.0</option> seedance */} 
                  <option value="veo3">StyleUAI 2.0 </option>
                </select>
              </div>

              <button
                onClick={handleGenerate}
                disabled={!image || processing}
                className={`mt-4 w-full flex items-center justify-center
                  bg-[#3913C5] text-white py-2 rounded-lg
                  ${(!image || processing) && "opacity-50 cursor-not-allowed"}`}
              >
                {processing ? (
                  <>
                    <Loader className="animate-spin w-5 h-5 mr-2" />
                    Generating {generationProgress}%
                  </>
                ) : (
                  <>
                    <Video className="w-5 h-5 mr-2" />
                    Generate Video
                  </>
                )}
              </button>

              {assets.length > 0 && (
                <div className="mt-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-3">Your Assets</h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                    {paginatedAssets.map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt="Asset"
                        onClick={() => handleAssetSelect(url)}
                        className="cursor-pointer w-full h-[200px] object-contain rounded-lg border hover:border-[#3913C5]"
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="text-sm px-3 py-1 rounded bg-gray-100 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="text-sm px-3 py-1 rounded bg-gray-100 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Your Generated Videos</h2>
              {videosLoading ? (
                <p className="text-gray-400 text-sm">Loading…</p>
              ) : videos.length === 0 ? (
                <p className="text-gray-400 text-sm">No videos yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                   {paginatedVideos.map((url) => (
                  <video
                    key={url}
                    src={url}
                    controls
                    className="w-full h-auto max-h-40 object-contain rounded"
                  />
                ))}
                </div>
              )}
              {videos.length > videosPerPage && (
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={handlePrevVideoPage}
                  disabled={videoPage === 1}
                  className="text-sm px-3 py-1 rounded bg-gray-100 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {videoPage} of {totalVideoPages}
                </span>
                <button
                  onClick={handleNextVideoPage}
                  disabled={videoPage === totalVideoPages}
                  className="text-sm px-3 py-1 rounded bg-gray-100 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
                        </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default VideoGenerator;
